/* globals zoomSdk */
import { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { selectZaClientVersion } from '../root/reducer';
import { determineTimerColor, GREEN } from '../utils';
import { LINE_WIDTH_FOR_1280P } from './Constants';
import VideoSettings from './VideoSettings';

export default function DigitalCanvas({
  hours,
  minutes,
  seconds,
  startTime,
  timerTime,
  watchOnVideo,
  watchType,
  vfOnVideo,
  setVfOnVideo,
  size,
  width,
  isVideoDimensionsChange,
  isVideoTurnedOn,
  activeToasts,
  setActiveToasts,
}) {
  const [showError, setShowError] = useState(false);
  const [persistenceCheck, setPersistenceCheck] = useState(false);
  const clientVersion = useSelector(selectZaClientVersion);
  const timeHeight = useRef(0);
  const [scale, setScale] = useState(
    window.devicePixelRatio === 1 ? 2 : window.devicePixelRatio
  );
  const fontSettings =
    "px -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif";
  const [lineWidth, setLineWidth] = useState(8);

  // This effect updates lineWidth used by timer VF based on width from video dimensions
  useEffect(() => {
    const ratio = width / 1280;
    setLineWidth(Math.floor(LINE_WIDTH_FOR_1280P * ratio));
  }, [width]);

  /**
   * This effect adds an event listener for devicePixelRatio change and sets the scale accordingly.
   */
  useEffect(() => {
    const handleResize = debounce(() => {
      setScale(window.devicePixelRatio === 1 ? 2 : window.devicePixelRatio);
    }, 500);

    const mqString = `(resolution: ${window.devicePixelRatio}dppx)`;
    const media = matchMedia(mqString);

    if (media.addEventListener) {
      media.addEventListener('change', handleResize);
    } else {
      window.addEventListener('resize', handleResize);
    }

    return () => {
      if (media.removeEventListener) {
        media.removeEventListener('change', handleResize);
      } else {
        window.removeEventListener('resize', handleResize);
      }
      handleResize.cancel();
    };
  }, []);

  useEffect(() => {
    if (parseInt(clientVersion.split('.').join('')) >= 51160000) {
      setPersistenceCheck(true);
    }
  }, [clientVersion]);

  useEffect(() => {
    if (isVideoDimensionsChange || timeHeight.current === 0) {
      let c = document.getElementById('canvas_height');
      let ctx = c.getContext('2d');
      ctx.font = '400 ' + size * 0.16 * scale + fontSettings;

      timeHeight.current =
        ctx.measureText('00:00:00').actualBoundingBoxAscent +
        ctx.measureText('00:00:00').actualBoundingBoxDescent;
    }

    let c = document.getElementById('canvas_countdown');
    let ctx = c.getContext('2d');

    c.style.width = size + 'px';
    c.style.height = size + 'px';

    c.width = Math.floor(size * scale);
    c.height = Math.floor(size * scale);

    let firstDigitString =
      Math.abs(hours) > 0
        ? `${String(Math.abs(hours)).padStart(2, '0')}`
        : `${String(Math.abs(minutes)).padStart(2, '0')}`;

    let secondDigitString =
      Math.abs(hours) > 0
        ? `${String(Math.abs(minutes)).padStart(2, '0')}`
        : `${String(Math.abs(seconds)).padStart(2, '0')}`;

    let thirdDigitString =
      Math.abs(hours) > 0
        ? `${String(Math.abs(seconds)).padStart(2, '0')}`
        : '';

    // Calculate height for the text
    let textHeight = timeHeight.current;

    // Code used to calculate the proper rectWidth
    // Set the font before measuring the text to ensure correct dimensions
    ctx.font = '400 ' + size * 0.16 * scale + fontSettings;
    let firstWidth = ctx.measureText('00').width;
    let colonWidth = ctx.measureText(':').width;
    let negativeWidth = ctx.measureText('-').width;

    let rectX = 30;
    let rectY = 50;
    let rectPadding = 80;
    let rectWidth =
      rectPadding +
      (timerTime < 0
        ? Math.abs(hours) > 0
          ? ctx.measureText('-00:00:00').width
          : ctx.measureText('-00:00').width
        : hours > 0
        ? ctx.measureText('00:00:00').width
        : ctx.measureText('00:00').width);
    let rectHeight = 140;
    let rectRadii = 8;
    let innerRadii = 6;
    let colonPadding = 10;

    function drawRoundedRect(ctx, x, y, width, height, radii) {
      const r = Math.min(radii, width / 2, height / 2); // Ensure radii are not larger than width/height
      ctx.beginPath();
      ctx.moveTo(x + r, y); // start at the top-left corner
      ctx.lineTo(x + width - r, y); // top edge
      ctx.arcTo(x + width, y, x + width, y + r, r); // top-right corner
      ctx.lineTo(x + width, y + height - r); // right edge
      ctx.arcTo(x + width, y + height, x + width - r, y + height, r); // bottom-right corner
      ctx.lineTo(x + r, y + height); // bottom edge
      ctx.arcTo(x, y + height, x, y + height - r, r); // bottom-left corner
      ctx.lineTo(x, y + r); // left edge
      ctx.arcTo(x, y, x + r, y, r); // top-left corner
      ctx.closePath(); // close the path
    }

    ctx.beginPath();
    drawRoundedRect(ctx, rectX, rectY, rectWidth, rectHeight, rectRadii);
    ctx.fillStyle = 'black';
    ctx.fill();

    // Set the border color using determineTimerColor
    if (watchType === 'stopwatch') {
      ctx.strokeStyle = '#0E72ED'; // if it's a stopwatch, show blue color.
    } else {
      ctx.strokeStyle = determineTimerColor(timerTime, startTime);
    }
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    ctx.stroke();

    // Draw the inner rectangle
    ctx.beginPath();
    drawRoundedRect(
      ctx,
      rectX + innerRadii,
      rectY + innerRadii,
      rectWidth - 2 * innerRadii,
      rectHeight - 2 * innerRadii,
      rectRadii
    );
    ctx.strokeStyle = 'black';
    ctx.lineWidth = innerRadii;
    ctx.stroke();

    // Set the text color using determineTimerColor
    if (watchType === 'stopwatch') {
      ctx.fillStyle = '#0E72ED';
    } else {
      ctx.fillStyle = determineTimerColor(timerTime, startTime);
    }
    ctx.font = '400 ' + size * 0.16 * scale + fontSettings;

    // Draw the timer text inside the rectangle
    // Calculate base X coordinate for centering
    let totalWidth =
      (timerTime < 0 ? negativeWidth : 0) +
      firstWidth +
      colonWidth +
      colonPadding * 2 +
      firstWidth +
      (thirdDigitString ? colonWidth + colonPadding * 2 + firstWidth : 0);
    let textX = rectX + (rectWidth - totalWidth) / 2;
    let textY = rectY + (rectHeight + textHeight) / 2;

    // Start drawing from textX
    let xPosition = textX;

    if (timerTime < 0) {
      ctx.fillText('-', xPosition, textY);
      xPosition += negativeWidth;
    }

    // Draw first segment (hours or minutes)
    ctx.fillText(firstDigitString, xPosition, textY);
    xPosition += firstWidth;

    // Draw first colon with padding
    xPosition += colonPadding;
    ctx.fillText(':', xPosition, textY);
    xPosition += colonWidth + colonPadding;

    // Draw second segment (minutes or seconds)
    ctx.fillText(secondDigitString, xPosition, textY);
    xPosition += firstWidth;

    // Draw second colon with padding and seconds if hours are present
    if (thirdDigitString) {
      xPosition += colonPadding;
      ctx.fillText(':', xPosition, textY);
      xPosition += colonWidth + colonPadding;

      // Draw third segment (seconds)
      ctx.fillText(thirdDigitString, xPosition, textY);
    }

    return () => {
      ctx.clearRect(0, 0, c.width, c.height);
    };
  }, [
    size,
    scale,
    startTime,
    timerTime,
    hours,
    minutes,
    seconds,
    width,
    lineWidth,
    timeHeight,
    isVideoDimensionsChange,
  ]);

  useEffect(() => {
    let c = document.getElementById('canvas_countdown');
    let smallCanvas = document.getElementById('canvas_small');
    let smallContext = smallCanvas.getContext('2d');

    if (watchOnVideo) {
      smallCanvas.style.width = size + 'px';
      smallCanvas.style.height = size + 'px';

      smallCanvas.width = Math.floor(size * scale);
      smallCanvas.height = Math.floor(size * scale);

      smallContext.scale(0.7, 0.7);
      smallContext.drawImage(c, 0, 0);

      const canvasImageData = smallContext.getImageData(
        0,
        0,
        size * scale,
        size * scale
      );

      async function setVF() {
        try {
          let setVFResponse;
          if (persistenceCheck) {
            setVFResponse = await zoomSdk.callZoomApi('setVirtualForeground', {
              imageData: canvasImageData,
              persistence: 'app',
            });
          } else {
            setVFResponse = await zoomSdk.callZoomApi('setVirtualForeground', {
              imageData: canvasImageData,
            });
          }
          setVfOnVideo(true);
          setShowError(false);
          return setVFResponse;
        } catch (error) {
          if (
            parseInt(hours) > 0 ||
            parseInt(minutes) > 0 ||
            parseInt(seconds) > 0
          ) {
            setShowError(true);
          }
        }
      }
      setVF();
    } else {
      if (vfOnVideo) {
        zoomSdk.callZoomApi('removeVirtualForeground').catch((error) => {
          /*Do Nothing. Currently, as of June 7, 2023, the foreground APIs do it function but still throws an error saying something went wrong.
          Issue with APIs/Client team for fix. An error dialog box can be put in the future here.*/
        });
        setVfOnVideo(false);
      }
    }
    return () => {
      smallContext.clearRect(0, 0, c.width, c.height);
    };
  }, [
    size,
    scale,
    hours,
    minutes,
    seconds,
    watchOnVideo,
    vfOnVideo,
    persistenceCheck,
  ]);

  return (
    <>
      <VideoSettings
        showError={showError || !isVideoTurnedOn}
        watchType={watchType}
        showBanner={watchOnVideo}
        activeToasts={activeToasts}
        setActiveToasts={setActiveToasts}
      />
      <canvas hidden id='canvas_countdown'></canvas>
      <canvas hidden id='canvas_small'></canvas>
      <canvas hidden id='canvas_height'></canvas>
    </>
  );
}
